import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Grid, 
    Typography, 
    Paper, 
    Card,
    CardContent,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { 
    FaUsers, 
    FaStore, 
    FaTicketAlt, 
    FaCreditCard,
    FaChartLine,
    FaExclamationTriangle
} from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { getDashboardStats, getSystemHealth } from '../../services/api';

// Styled components matching vendor design
const DashboardContainer = styled(Box)({
    padding: '2rem 1rem',
    maxWidth: '1400px',
    margin: '0 auto',
});

const PageTitle = styled(Typography)({
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '2rem',
    textAlign: 'center',
});

const StatsCard = styled(Card)({
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
    height: '100%',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    },
});

const StatValue = styled(Typography)({
    fontSize: '2.8rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '0.5rem',
});

const StatLabel = styled(Typography)({
    fontSize: '1.1rem',
    color: AppColors.textSecondary,
    fontWeight: '500',
});

const IconBox = styled(Box)(({ color }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${color}20, ${color}10)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color,
    fontSize: '2rem',
    marginBottom: '1.5rem',
    boxShadow: `0 8px 20px ${color}20`,
}));

const ChartContainer = styled(Paper)({
    padding: '2rem',
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
    marginTop: '2rem',
    height: '450px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
});

const SectionTitle = styled(Typography)({
    fontSize: '1.5rem',
    fontWeight: 600,
    color: AppColors.primaryDark,
    marginBottom: '1.5rem',
});

const SystemHealthCard = styled(Card)(({ status }) => ({
    borderRadius: '15px',
    marginBottom: '1rem',
    boxShadow: 'none',
    border: `2px solid ${
        status === 'healthy' ? AppColors.successGreen :
        status === 'warning' ? AppColors.warningOrange :
        AppColors.errorRed
    }`,
    background: `linear-gradient(135deg, ${
        status === 'healthy' ? `${AppColors.successGreen}10` :
        status === 'warning' ? `${AppColors.warningOrange}10` :
        `${AppColors.errorRed}10`
    }, rgba(255,255,255,0.8))`,
}));

const CHART_COLORS = [AppColors.primaryDark, AppColors.primaryYellow, '#2196F3', '#4CAF50'];

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [statsData, healthData] = await Promise.all([
                    getDashboardStats(),
                    getSystemHealth()
                ]);
                setStats(statsData);
                setSystemHealth(healthData);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <DashboardContainer>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress size={60} sx={{ color: AppColors.primaryDark }} />
                </Box>
            </DashboardContainer>
        );
    }

    if (error) {
        return (
            <DashboardContainer>
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 3, 
                        borderRadius: '15px',
                        fontSize: '1.1rem'
                    }}
                >
                    {error}
                </Alert>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer>
            <PageTitle>Admin Dashboard</PageTitle>

            {/* Stats Cards */}
            <Grid container spacing={4}>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatsCard>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconBox color={AppColors.successGreen}>
                                <FaUsers />
                            </IconBox>
                            <StatValue>{stats?.total_users || 0}</StatValue>
                            <StatLabel>Total Users</StatLabel>
                        </CardContent>
                    </StatsCard>
                </Grid>
                
                <Grid item xs={12} sm={6} lg={3}>
                    <StatsCard>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconBox color="#2196F3">
                                <FaStore />
                            </IconBox>
                            <StatValue>{stats?.total_vendors || 0}</StatValue>
                            <StatLabel>Active Vendors</StatLabel>
                        </CardContent>
                    </StatsCard>
                </Grid>
                
                <Grid item xs={12} sm={6} lg={3}>
                    <StatsCard>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconBox color={AppColors.primaryYellow}>
                                <FaTicketAlt />
                            </IconBox>
                            <StatValue>{stats?.total_vouchers || 0}</StatValue>
                            <StatLabel>Total Vouchers</StatLabel>
                        </CardContent>
                    </StatsCard>
                </Grid>
                
                <Grid item xs={12} sm={6} lg={3}>
                    <StatsCard>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <IconBox color="#FF5722">
                                <FaCreditCard />
                            </IconBox>
                            <StatValue>AED {stats?.total_revenue || '0'}</StatValue>
                            <StatLabel>Total Revenue</StatLabel>
                        </CardContent>
                    </StatsCard>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={4} sx={{ mt: 1 }}>
                <Grid item xs={12} md={8}>
                    <ChartContainer>
                        <SectionTitle>User Growth Analytics</SectionTitle>
                        <Box height={320}>
                            {stats?.user_growth ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stats.user_growth}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                                        <XAxis dataKey="date" stroke={AppColors.textSecondary} />
                                        <YAxis stroke={AppColors.textSecondary} />
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'rgba(255,255,255,0.95)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="users" 
                                            stroke={AppColors.primaryDark} 
                                            strokeWidth={3}
                                            activeDot={{ r: 8, fill: AppColors.primaryYellow }} 
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Typography variant="h6" color="textSecondary">No data available</Typography>
                                </Box>
                            )}
                        </Box>
                    </ChartContainer>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Paper sx={{ 
                        p: 3, 
                        borderRadius: '20px', 
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)', 
                        height: '450px',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                    }}>
                        <SectionTitle>System Health Monitor</SectionTitle>
                        
                        {systemHealth ? (
                            <Box>
                                <SystemHealthCard status={systemHealth.database_status}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={600} color={AppColors.primaryDark}>Database</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Status: {systemHealth.database_status}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Response: {systemHealth.database_response_time}ms
                                        </Typography>
                                    </CardContent>
                                </SystemHealthCard>

                                <SystemHealthCard status={systemHealth.api_status}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={600} color={AppColors.primaryDark}>API Server</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Status: {systemHealth.api_status}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Uptime: {systemHealth.api_uptime}
                                        </Typography>
                                    </CardContent>
                                </SystemHealthCard>

                                <SystemHealthCard status={systemHealth.storage_status}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight={600} color={AppColors.primaryDark}>Storage</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Status: {systemHealth.storage_status}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Usage: {systemHealth.storage_usage}%
                                        </Typography>
                                    </CardContent>
                                </SystemHealthCard>
                            </Box>
                        ) : (
                            <Typography variant="body1" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                                System health data unavailable
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Additional Charts */}
            <Grid container spacing={4} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                    <ChartContainer>
                        <SectionTitle>Monthly Revenue Trends</SectionTitle>
                        <Box height={320}>
                            {stats?.monthly_revenue ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats.monthly_revenue}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                                        <XAxis dataKey="month" stroke={AppColors.textSecondary} />
                                        <YAxis stroke={AppColors.textSecondary} />
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'rgba(255,255,255,0.95)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Bar 
                                            dataKey="revenue" 
                                            fill={AppColors.primaryYellow}
                                            radius={[8, 8, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Typography variant="h6" color="textSecondary">No revenue data available</Typography>
                                </Box>
                            )}
                        </Box>
                    </ChartContainer>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <ChartContainer>
                        <SectionTitle>User Distribution</SectionTitle>
                        <Box height={320}>
                            {stats?.user_types ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={stats.user_types}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            outerRadius={120}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {stats.user_types.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{
                                                backgroundColor: 'rgba(255,255,255,0.95)',
                                                border: 'none',
                                                borderRadius: '10px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Typography variant="h6" color="textSecondary">No user type data available</Typography>
                                </Box>
                            )}
                        </Box>
                    </ChartContainer>
                </Grid>
            </Grid>
        </DashboardContainer>
    );
};

export default Dashboard;
